/** @format */
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { OtmId } from 'common-typescript/types';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { SessionStorageService } from 'sis-common';
import { confirmDialogFdOpener, ConfirmDialogFdValues } from 'sis-components/confirm/confirm-dialog-fd.component';

type AccessTokensByProductId = { [productId: OtmId]: string };

const CONFIG = {
    ENDPOINTS: {
        backend: '/ilmo/api/open-university/product-access-tokens',
        getOpenUniversityProductIdsRequiringAccessTokenForPurchase() {
            return `${this.backend}/product-ids-requiring-access-token-for-purchase`;
        },
        isAccessTokenValidForPurchase() {
            return `${this.backend}/is-valid-for-purchase`;
        },
    },
};

@Injectable({
    providedIn: 'root',
})
export class StudentOpenUniversityProductAccessTokenService {
    private readonly http = inject(HttpClient);
    private readonly confirmDialogFdOpener = confirmDialogFdOpener();
    private readonly sessionStorageService = inject(SessionStorageService);

    private readonly ACCESS_TOKENS_SESSION_STORAGE_KEY = 'OpenUniversityProductAccessTokens';

    private readonly _accessTokensByProductId$ = new BehaviorSubject<AccessTokensByProductId>(this.getAccessTokens());

    /**
     * Returns an observable that emits a valid access token for the given product if the user has one, or null otherwise.
     */
    getAccessTokenForProduct(productId: OtmId): Observable<string | null> {
        return this._accessTokensByProductId$.asObservable().pipe(
            map(tokens => tokens[productId] ?? null),
            distinctUntilChanged(),
        );
    }

    /**
     * Save product access token to session storage for later use
     */
    saveAccessTokenToSessionStorage(productId: OtmId, accessToken: string): void {
        const existingTokens: AccessTokensByProductId = this.getAccessTokens();
        const tokens: AccessTokensByProductId = { ...existingTokens, [productId]: accessToken };
        this.sessionStorageService.setValue(this.ACCESS_TOKENS_SESSION_STORAGE_KEY, tokens);
        this._accessTokensByProductId$.next(tokens);
    }

    /**
     * Checks which open university products require an access token for purchase.
     * @param productIds Open university product IDs to check
     * @returns IDs of those products from the input set which require an access token for purchase
     */
    getOpenUniversityProductIdsRequiringAccessTokenForPurchase(productIds: OtmId[]): Observable<OtmId[]> {
        return this.http.post<OtmId[]>(CONFIG.ENDPOINTS.getOpenUniversityProductIdsRequiringAccessTokenForPurchase(), productIds);
    }

    openAccessTokenValidationErrorModal() {
        this.confirmDialogFdOpener({
            title: 'OPEN_UNIVERSITY.ERRORS.INVALID_ACCESS_TOKEN_LINK.TITLE',
            description: 'OPEN_UNIVERSITY.ERRORS.INVALID_ACCESS_TOKEN_LINK.DESC',
            confirmObservable: of(true),
            confirmText: 'BUTTON.CONTINUE',
            hideCancel: true,
        } as ConfirmDialogFdValues<boolean>);
    }

    /**
     * Checks if an open university product can be purchased with an access token.
     */
    isAccessTokenValidForPurchase(openUniversityProductId: OtmId, accessToken: string): Observable<boolean> {
        const params: Record<string, string> = { openUniversityProductId, accessToken };
        return this.http.get<boolean>(CONFIG.ENDPOINTS.isAccessTokenValidForPurchase(), { params });
    }

    private getAccessTokens(): AccessTokensByProductId {
        return this.sessionStorageService.getValueOrDefault(this.ACCESS_TOKENS_SESSION_STORAGE_KEY, {});
    }
}
